/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Vlaidslav Oshkanov
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */
import { memo } from 'react';

import concat from 'lodash/concat';
import isEqual from 'lodash/isEqual';

import { Select } from '@components/common';
import { IOptionItemProps } from '@components/common/Select/Select';
import initTranslationCommon from '@modules/internationalization/constants/common';
import ICommonTranslation from '@modules/internationalization/types/common';

/**
 * @description Multiselect filter props
 * @interface IFilterSelectProps
 * @property {IInputProps} input - toggle input props
 * @property {IOptionItemProps[]} selectOptions - select options
 * @property {IOptionItemProps} selectedOption - selected option
 * @property {() => void} onChange - change method
 */
export interface IFilterSelectProps {
    onChange?: (value: IOptionItemProps) => void;
    selectedOption?: IOptionItemProps;
    selectOptions?: IOptionItemProps[];
    translation: ICommonTranslation;
}

const defaultProps: Partial<IFilterSelectProps> = {
    onChange: () => null,
    translation: initTranslationCommon,
};

const emptyOption: IOptionItemProps = {
    label: '',
    value: '',
};

const checkAllOption = (label: string): IOptionItemProps => ({
    label: label,
    value: '',
});

/**
 * @description Filter with select.
 * @extends IFilterSelectProps
 * @see BlockWithIcon component
 * @see RangeDatePicker component
 * @see TextBlock component
 */
const FilterSelect = ({ translation, selectOptions, selectedOption, onChange }: IFilterSelectProps) => {

    const onSelectHandler = (e: IOptionItemProps) => {
        if (!isEqual(e, selectedOption) && (e.value || e.value === 0) && onChange) {
            onChange(e);
        }
        if (selectedOption && !e.value && onChange) {
            onChange(emptyOption);
        }
    };

    const options: IOptionItemProps[] = selectOptions
        ? concat(checkAllOption(translation.select.placeholder), selectOptions)
        : [checkAllOption(translation.select.placeholder)];

    return (
        <Select
            kind="normal"
            options={options}
            placeholder={translation.select.placeholder}
            renderInBody={true}
            selectedOption={selectedOption}
            translation={translation}
            onSelect={onSelectHandler}
        />
    );
};

FilterSelect.defaultProps = defaultProps;

export default memo(FilterSelect);
